import { t, Instance, flow, SnapshotIn } from 'mobx-state-tree';
import { UserModel } from './models/User';
import { FamilyMetadataModel } from './models/FamilyMetadata';
import { api } from '@/api';
import type {
  FamilyResponse,
  DeleteAccountRequest,
  BillingInterval,
  CheckoutParamsResponse,
  FamilyPaymentHistoryResponse,
  OneTimeCheckoutParamsResponse,
  FamilyUserResponse,
  UpdateFamilyUserRoleRequest
} from 'we-tree-api-client/v2';
import { generateUUID } from '@/utils/uuid';
import { WeTreeApiError } from '@/api/errors';

/**
 * DomainStore - Top-level store following pantryPlus pattern
 * Aggregates user, selected family, and family metadata
 */
export const DomainStore = t
  .model('DomainStore', {
    user: t.maybe(UserModel),
    selectedFamilyId: t.maybeNull(t.string),
    familiesMetadata: t.array(FamilyMetadataModel),
    isLoadingFamiliesMetaData: t.optional(t.boolean, false),
    // User initialization state
    isInitializingUser: t.optional(t.boolean, false),
    userInitializationError: t.maybeNull(t.string)
  })
  .views((self) => ({
    get currentFamily() {
      if (!self.selectedFamilyId) return null;
      return self.familiesMetadata.find((f) => f.id === self.selectedFamilyId);
    }
  }))
  .actions((self) => ({
    // Basic actions
    setSelectedFamily(familyId: string | null) {
      self.selectedFamilyId = familyId;
    },

    // API integration
    loadFamiliesMetadata: flow(function* () {
      self.isLoadingFamiliesMetaData = true;
      try {
        const result = yield api.families.getUserFamilies();
        if (result.success) {
          const metadata = result.data.map((family: FamilyResponse) =>
            FamilyMetadataModel.create({
              id: family.id,
              name: family.name,
              displayName: family.displayName ?? family.name,
              alternativeName: family.alternativeName ?? null,
              plan: family.plan || 'free',
              memberCount: family.memberCount || 0,
              createdAt: family.createdAt,
              updatedAt: family.updatedAt,
              createdBy: family.createdBy || null,
              createdByNickname: family.createdByNickname || null,
              updatedBy: family.updatedBy || null,
              updatedByNickname: family.updatedByNickname || null,
              lastActivityAt: family.lastActivityAt || family.createdAt || '',
              lastActivityBy: family.lastActivityBy ?? null,
              userRole: family.userRole || null,
              canDeleteFamily: (family as FamilyResponse & { canDeleteFamily?: boolean }).canDeleteFamily ?? false
            })
          );
          self.familiesMetadata.replace(metadata);
        } else {
          // Handle typed error with user-friendly message
          const errorMessage = result.error.getUserMessage();
          // eslint-disable-next-line no-console
          console.error('Failed to load family metadata:', errorMessage, result.error);

          // Could set an error state here for UI display
          // self.familiesMetadataError = errorMessage;
        }
      } catch (error) {
        // This should rarely happen now with the wrapped API
        // eslint-disable-next-line no-console
        console.error('Unexpected error loading family metadata:', error);
      } finally {
        self.isLoadingFamiliesMetaData = false;
      }
    }),

    /**
     * Update or add family metadata to the familiesMetadata array
     * Called when FamilyStore loads family data to keep metadata in sync
     */
    updateFamilyMetadata(familyData: {
      id: string;
      name: string;
      displayName?: string;
      alternativeName?: string | null;
      plan?: string;
      memberCount?: number;
      createdAt?: string;
      updatedAt?: string;
      createdBy?: string | null;
      createdByNickname?: string | null;
      updatedBy?: string | null;
      updatedByNickname?: string | null;
      lastActivityAt?: string | null;
      lastActivityBy?: string | null;
      userRole?: string | null;
      canDeleteFamily?: boolean;
    }) {
      const existingIndex = self.familiesMetadata.findIndex((f) => f.id === familyData.id);
      const existing = existingIndex >= 0 ? self.familiesMetadata[existingIndex] : null;
      const metadata = FamilyMetadataModel.create({
        id: familyData.id,
        name: familyData.name,
        displayName: familyData.displayName ?? familyData.name,
        alternativeName: familyData.alternativeName ?? existing?.alternativeName ?? null,
        plan: familyData.plan ?? existing?.plan ?? 'free',
        memberCount: familyData.memberCount ?? existing?.memberCount ?? 0,
        createdAt: familyData.createdAt ?? existing?.createdAt ?? '',
        updatedAt: familyData.updatedAt ?? existing?.updatedAt ?? '',
        createdBy: familyData.createdBy !== undefined ? familyData.createdBy : (existing?.createdBy ?? null),
        createdByNickname:
          familyData.createdByNickname !== undefined
            ? familyData.createdByNickname
            : (existing?.createdByNickname ?? null),
        updatedBy: familyData.updatedBy !== undefined ? familyData.updatedBy : (existing?.updatedBy ?? null),
        updatedByNickname:
          familyData.updatedByNickname !== undefined
            ? familyData.updatedByNickname
            : (existing?.updatedByNickname ?? null),
        lastActivityAt:
          familyData.lastActivityAt !== undefined
            ? (familyData.lastActivityAt ?? '')
            : (existing?.lastActivityAt ?? ''),
        lastActivityBy:
          familyData.lastActivityBy !== undefined ? familyData.lastActivityBy : (existing?.lastActivityBy ?? null),
        userRole: familyData.userRole !== undefined ? familyData.userRole : (existing?.userRole ?? null),
        canDeleteFamily: familyData.canDeleteFamily ?? existing?.canDeleteFamily ?? false
      });

      if (existingIndex >= 0) {
        // Update existing metadata
        self.familiesMetadata[existingIndex] = metadata;
      } else {
        // Add new metadata
        self.familiesMetadata.push(metadata);
      }
    },

    createFamily: flow(function* (familyData: { name: string; slug: string }) {
      try {
        const familyId = generateUUID();
        const result = yield api.families.createFamily({
          id: familyId,
          name: familyData.name,
          slug: familyData.slug
        });

        if (!result.success) {
          // Handle typed error - could be validation, auth, network, etc.
          throw new Error(result.error.getUserMessage());
        }

        const response = result.data;

        // Add the new family to metadata as a FamilyMetadataModel instance
        const newMetadata = FamilyMetadataModel.create({
          id: response.id,
          name: response.name,
          displayName: response.displayName ?? response.name,
          alternativeName: response.alternativeName ?? null,
          plan: response.plan || 'free',
          memberCount: response.memberCount || 0,
          createdAt: response.createdAt,
          updatedAt: response.updatedAt,
          createdBy: response.createdBy || null,
          createdByNickname: response.createdByNickname || null,
          updatedBy: response.updatedBy || null,
          updatedByNickname: response.updatedByNickname || null,
          lastActivityAt: response.lastActivityAt || response.createdAt || '',
          lastActivityBy: response.lastActivityBy ?? response.createdByNickname ?? response.createdBy ?? null,
          userRole: 'owner', // Family creator always has owner role
          canDeleteFamily: true
        });
        self.familiesMetadata.push(newMetadata);
        return response;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to create family:', error);
        throw error;
      }
    }),

    setUser(userData: SnapshotIn<typeof UserModel>) {
      self.user = UserModel.create(userData);
      // Clear error when user is successfully set
      self.userInitializationError = null;
    },

    /**
     * Update the current user's profile (email and/or nickname)
     * Called after successful Cognito + API sync
     */
    updateUserProfile(updates: { email?: string; nickname?: string }) {
      if (!self.user) return;
      if (updates.email !== undefined) self.user.email = updates.email;
      if (updates.nickname !== undefined) self.user.nickname = updates.nickname;
    },

    setUserInitializationError(errorMessage: string) {
      self.userInitializationError = errorMessage;
      self.isInitializingUser = false;
    },

    setUserInitializationState(isInitializing: boolean) {
      self.isInitializingUser = isInitializing;
      if (isInitializing) {
        self.userInitializationError = null;
      }
    },

    clearUserInitializationError() {
      self.userInitializationError = null;
    },

    /**
     * Owner-only: ask the API for Paddle.js checkout params (price + custom_data).
     * Does not grant entitlements — caller opens Paddle and waits for webhooks.
     */
    prepareUpgradeCheckout: flow(function* (familyId: string, interval: BillingInterval) {
      const result: { success: true; data: CheckoutParamsResponse } | { success: false; error: WeTreeApiError } =
        yield api.billing.createCheckout({ familyId, interval });
      if (!result.success) {
        throw new Error(result.error.getUserMessage());
      }
      return result.data as CheckoutParamsResponse;
    }),

    /**
     * Owner-only: mint a fresh Paddle customer portal URL and return it (caller redirects).
     */
    prepareBillingPortal: flow(function* (familyId: string) {
      const result: { success: true; data: { url: string } } | { success: false; error: WeTreeApiError } =
        yield api.billing.createPortalSession({ familyId });
      if (!result.success) {
        throw new Error(result.error.getUserMessage());
      }
      return result.data.url as string;
    }),

    /**
     * Owner-only: load FAMILY_PAYMENT contribution / payment history and balance.
     */
    loadBillingPaymentHistory: flow(function* (familyId: string, options?: { page?: number; pageSize?: number }) {
      const result: { success: true; data: FamilyPaymentHistoryResponse } | { success: false; error: WeTreeApiError } =
        yield api.billing.listFamilyPayments(familyId, options);
      if (!result.success) {
        throw new Error(result.error.getUserMessage());
      }
      return result.data as FamilyPaymentHistoryResponse;
    }),

    /**
     * Any family member: prepare a one-time contribution checkout (Paddle transactionId).
     * Does not credit balance locally — wait for verified webhooks.
     */
    prepareContributeCheckout: flow(function* (familyId: string, amountCents: number) {
      const result: { success: true; data: OneTimeCheckoutParamsResponse } | { success: false; error: WeTreeApiError } =
        yield api.billing.createContributeCheckout({
          familyId,
          amountCents
        });
      if (!result.success) {
        throw new Error(result.error.getUserMessage());
      }
      return result.data as OneTimeCheckoutParamsResponse;
    }),

    /**
     * Any authenticated user: prepare a one-time donation checkout (no family effect).
     */
    prepareDonateCheckout: flow(function* (amountCents: number) {
      const result: { success: true; data: OneTimeCheckoutParamsResponse } | { success: false; error: WeTreeApiError } =
        yield api.billing.createDonateCheckout({
          amountCents
        });
      if (!result.success) {
        throw new Error(result.error.getUserMessage());
      }
      return result.data as OneTimeCheckoutParamsResponse;
    }),

    deleteFamily: flow(function* (familyId: string) {
      yield api.families.deleteFamily(familyId);
      const remaining = self.familiesMetadata.filter((f) => f.id !== familyId);
      self.familiesMetadata.replace(remaining);
    }),

    /**
     * Change a collaborator's family role (equal-or-lesser; tier-gated on API).
     */
    updateFamilyUserRole: flow(function* (familyId: string, userId: string, role: string) {
      const body: UpdateFamilyUserRoleRequest = {
        role: role as UpdateFamilyUserRoleRequest['role']
      };
      const result: { success: true; data: FamilyUserResponse } | { success: false; error: WeTreeApiError } =
        yield api.families.updateFamilyUserRole(familyId, userId, body);
      if (!result.success) {
        throw new Error(result.error.getUserMessage());
      }
      return result.data;
    }),

    /**
     * Load preview of what account deletion will do to families ownership.
     */
    getAccountDeletionImpact: flow(function* () {
      const result = yield api.users.getAccountDeletionImpact();
      if (!result.success) {
        throw new Error(result.error.getUserMessage());
      }
      return result.data;
    }),

    /**
     * Permanently delete the signed-in account (soft-delete + Cognito revoke).
     * Caller should sign out after success.
     */
    deleteAccount: flow(function* (request: DeleteAccountRequest) {
      const result = yield api.users.deleteAccount(request);
      if (!result.success) {
        throw new Error(result.error.getUserMessage());
      }
      self.user = undefined;
      self.selectedFamilyId = null;
      self.familiesMetadata.clear();
      return result.data;
    }),

    /**
     * Clear all user-related data and reset to initial state
     * Called when user signs out to ensure fresh state for next user
     */
    clear() {
      self.user = undefined;
      self.selectedFamilyId = null;
      self.familiesMetadata.clear();
      self.isLoadingFamiliesMetaData = false;
      self.isInitializingUser = false;
      self.userInitializationError = null;
    }
  }));

export type DomainStoreType = Instance<typeof DomainStore>;
export type FamilyMetadataType = Instance<typeof FamilyMetadataModel>;

// Export singleton instance
export const domainStore = DomainStore.create({});
